import { AxiosError } from 'axios';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { api } from '@utils/api';
import { ErrorResponse } from 'store/ReduxTypes';
import { VitaminPlanResponse, Vitamin } from './VitaminsTypes';
import T from '@data/text/commonTexts.json';

const POLLING_INTERVAL = 2000;
const MAX_ATTEMPTS = 30;

/* ---------- POST /api/v1/vitamin-requests ---------- */
export const requestVitaminPlanOperation = createAsyncThunk<
  VitaminPlanResponse,
  number,
  { rejectValue: ErrorResponse }
>('vitamins/requestPlan', async (instanceId, { rejectWithValue }) => {
  try {
    const { data } = await api.post<VitaminPlanResponse>(
      '/vitamin-requests',
      {},
      { params: { instance_id: instanceId } },
    );
    return data;
  } catch (err) {
    const e = err as AxiosError<ErrorResponse>;
    return rejectWithValue({ message: e.response?.data?.message || e.message });
  }
});

/* ---------- GET /api/v1/vitamin-requests/{requestId} ---------- */
export const getVitaminPlanByIdOperation = createAsyncThunk<
  VitaminPlanResponse,
  number,
  { rejectValue: ErrorResponse }
>('vitamins/planById', async (requestId, { rejectWithValue }) => {
  try {
    const { data } = await api.get<VitaminPlanResponse>(`/vitamin-requests/${requestId}`);
    return data;
  } catch (err) {
    const e = err as AxiosError<ErrorResponse>;
    return rejectWithValue({ message: e.response?.data?.message || e.message });
  }
});

/* ---------- GET /api/v1/vitamin-requests/latest ---------- */
export const getLatestVitaminPlanOperation = createAsyncThunk<
  Vitamin[],
  void,
  { rejectValue: ErrorResponse }
>('vitamins/latestPlan', async (_, { rejectWithValue }) => {
  try {
    const { data } = await api.get<Vitamin[]>('/vitamin-requests/latest');
    return data;
  } catch (err) {
    const e = err as AxiosError<ErrorResponse>;
    return rejectWithValue({ message: e.response?.data?.message || e.message });
  }
});

/* ---------- Комбинированный thunk с поллингом ---------- */
export const getOrRequestLatestVitaminPlanOperation = createAsyncThunk<
  Vitamin[],
  number, // instanceId
  { rejectValue: ErrorResponse }
>('vitamins/getOrRequest', async (instanceId, { rejectWithValue }) => {
  try {
    const { data } = await api.get<Vitamin[]>('/vitamin-requests/latest');
    return data;
  } catch (err) {
    const e = err as AxiosError<any>;

    if (e.response?.status !== 404) {
      return rejectWithValue({ message: e.response?.data?.message || e.message });
    }

    const { data: request } = await api.post<VitaminPlanResponse>(
      '/vitamin-requests',
      {},
      { params: { instance_id: instanceId } },
    );

    let attempts = 0;
    while (attempts < MAX_ATTEMPTS) {
      const { data: statusResp } = await api.get<VitaminPlanResponse>(
        `/vitamin-requests/${request.request_id}`,
      );

      if ((statusResp.status === 'created' || statusResp.status === 'completed') && statusResp.plan?.vitamins) {
        return statusResp.plan.vitamins;
      }

      if (statusResp.status === 'error') {
        return rejectWithValue({ message: statusResp.error || T.errors.vitaminsGenerationFailed });
      }

      attempts++;
      await new Promise(res => setTimeout(res, POLLING_INTERVAL));
    }

    return rejectWithValue({ message: T.errors.vitaminsGenerationTimeout });
  }
});


